// FileDrawer — el cajón de la ficha 360 (`_shared/ficha-cliente-360.js` del mock): encabezado
// fijo, una tira de pestañas y un cuerpo que scrollea solo. Lo usan las fichas de cliente, y el
// mismo armado sirve para cualquier detalle con pestañas.
//
// El cuerpo es el ÚNICO que scrollea. El alto del cajón no cambia al saltar de pestaña: sin eso,
// la tira de pestañas se mueve bajo el cursor cada vez que el contenido crece.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface FileDrawerTab {
    key: string;
    label: string;
}

/** `underline` es la de la ficha 360; `pill` la de los detalles del Portal Admin VL (`.detail-tab`). */
export type FileDrawerTabsVariant = 'underline' | 'pill';

const TAB_CLASS: Record<FileDrawerTabsVariant, { base: string; active: string; idle: string }> = {
    // `-mb-px` monta el borde del botón sobre el del contenedor, como el mock.
    underline: {
        base: '-mb-px shrink-0 whitespace-nowrap border-b-2 px-2 py-1.5 text-[11px] transition-colors',
        active: 'border-primary font-semibold text-foreground',
        idle: 'border-transparent font-medium text-muted-foreground hover:text-foreground',
    },
    pill: {
        base: 'shrink-0 whitespace-nowrap rounded-[5px] px-2.5 py-[5px] text-[11.5px] transition-colors',
        active: 'bg-accent font-semibold text-foreground',
        idle: 'font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground',
    },
};

/**
 * La tira de pestañas: en una fila que scrollea con sombra en el borde, o con `wrap` en renglones.
 * `wrap` es para cajones con más pestañas de las que caben a lo ancho.
 */
export function FileDrawerTabs({
    tabs,
    activeTab,
    onChange,
    variant = 'underline',
    wrap = false,
    className,
}: {
    tabs: readonly FileDrawerTab[];
    activeTab: string;
    onChange: (key: string) => void;
    variant?: FileDrawerTabsVariant;
    wrap?: boolean;
    className?: string;
}) {
    const tabClass = TAB_CLASS[variant];
    const stripRef = useRef<HTMLDivElement>(null);
    const [edges, setEdges] = useState({ start: false, end: false });

    const measure = useCallback(() => {
        const strip = stripRef.current;
        if (!strip) return;
        const overflow = strip.scrollWidth - strip.clientWidth;
        setEdges({
            start: strip.scrollLeft > 1,
            end: overflow > 1 && strip.scrollLeft < overflow - 1,
        });
    }, []);

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip || wrap) return;
        measure();
        strip.addEventListener('scroll', measure, { passive: true });
        // jsdom no trae ResizeObserver: sin la guarda, el render revienta en los tests.
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(strip);
        return () => {
            strip.removeEventListener('scroll', measure);
            observer?.disconnect();
        };
    }, [measure, tabs.length, wrap]);

    // La pestaña activa puede llegar por la URL con la tira scrolleada: se trae a la vista sola.
    useEffect(() => {
        if (wrap) return;
        const strip = stripRef.current;
        const button = strip?.querySelector<HTMLElement>('[data-active="true"]');
        button?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [activeTab, wrap]);

    return (
        <div className={cn('relative flex-none border-b border-border bg-card', className)}>
            <div
                ref={stripRef}
                role="tablist"
                className={cn(
                    'flex gap-0.5 px-2',
                    wrap
                        ? 'flex-wrap'
                        : 'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    variant === 'pill' && 'py-1.5',
                )}
            >
                {tabs.map((tab) => {
                    const active = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            data-active={active}
                            onClick={() => {
                                onChange(tab.key);
                            }}
                            className={cn(tabClass.base, active ? tabClass.active : tabClass.idle)}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>
            <EdgeShadow side="left" show={!wrap && edges.start} />
            <EdgeShadow side="right" show={!wrap && edges.end} />
        </div>
    );
}

/** El aviso de que la tira sigue más allá del borde. Decorativo: no captura el clic. */
function EdgeShadow({ side, show }: { side: 'left' | 'right'; show: boolean }) {
    if (!show) return null;
    return (
        <div
            aria-hidden
            className={cn(
                'pointer-events-none absolute inset-y-0 w-6',
                side === 'left'
                    ? 'left-0 bg-gradient-to-r from-card to-transparent'
                    : 'right-0 bg-gradient-to-l from-card to-transparent',
            )}
        />
    );
}

/**
 * El cajón completo: lo que va arriba fijo (`header`), la tira de pestañas y el cuerpo.
 *
 * El fondo del cuerpo es más oscuro que el de las tarjetas a propósito —`bg-muted/15` contra
 * `bg-card`—: con los dos del mismo color las tarjetas no se despegan y el cajón se lee plano.
 */
export function FileDrawer({
    header,
    tabs,
    activeTab,
    onTabChange,
    children,
    className,
}: {
    header?: ReactNode;
    tabs: readonly FileDrawerTab[];
    activeTab: string;
    onTabChange: (key: string) => void;
    children: ReactNode;
    className?: string;
}) {
    return (
        <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
            {header}
            <FileDrawerTabs tabs={tabs} activeTab={activeTab} onChange={onTabChange} />
            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-muted/15 p-3">
                {children}
            </div>
        </div>
    );
}
